import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import LanguageContext from '../context/LanguageContext';
import AuthContext from '../context/AuthContext';

export default function ArchiveMap() {
    const [archiveData, setArchiveData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { language } = useContext(LanguageContext);
    const { BASE_URL } = useContext(AuthContext);

    useEffect(() => {
        fetchArchiveMapData();
    }, []);

    const fetchArchiveMapData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/api/archive-map-data/`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setArchiveData(data);
        } catch (error) {
            console.error('Error fetching archive map data:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const renderArchiveMap = () => {
        if (!archiveData) return null;

        const { archive_items, documents } = archiveData;
        
        // Group documents by archive_item_id
        const documentsByArchive = {};
        documents.forEach(doc => {
            if (!documentsByArchive[doc.archive_item_id]) {
                documentsByArchive[doc.archive_item_id] = [];
            }
            documentsByArchive[doc.archive_item_id].push(doc);
        });

        return (
            <div style={{ 
                padding: '20px', 
                fontFamily: 'monospace',
                textAlign: language === 'persian' ? 'right' : 'left',
                direction: language === 'persian' ? 'rtl' : 'ltr'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <h1>Archive Map</h1>
                    <p style={{ 
                        fontSize: '14px', 
                        color: '#666', 
                        marginTop: '10px',
                        fontStyle: 'italic'
                    }}>
                        Click on archive items and documents to navigate to their respective pages
                    </p>
                </div>
                <div style={{ marginTop: '20px' }}>
                    {archive_items.map((archiveItem, archiveIndex) => (
                        <div key={archiveItem.id} className="archive-map-item">
                            {/* Archive Item */}
                            <Link 
                                to={`/archive/${archiveItem.id}`}
                                className="archive-map-link archive-map-archive-link"
                                onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                }}
                            >
                                <span className="archive-icon">📁</span> {language === 'persian' ? archiveItem.persian_title : archiveItem.title}
                            </Link>
                            
                            {/* Archive Item Years */}
                            {(archiveItem.start_year || archiveItem.end_year) && (
                                <div className="archive-map-years">
                                    {archiveItem.start_year && archiveItem.end_year 
                                        ? `${archiveItem.start_year} - ${archiveItem.end_year}`
                                        : archiveItem.start_year || archiveItem.end_year
                                    }
                                </div>
                            )}
                            
                            {/* Documents under this archive item */}
                            {documentsByArchive[archiveItem.id] && documentsByArchive[archiveItem.id].length > 0 && (
                                <div className="archive-map-documents">
                                    {documentsByArchive[archiveItem.id].map((document, docIndex) => (
                                        <div key={document.id} className="archive-map-document-item">
                                            <Link 
                                                to={`/document/${document.id}`}
                                                state={{ 
                                                    document: {
                                                        id: document.id,
                                                        name: document.name,
                                                        persian_name: document.persian_name,
                                                        author: document.author,
                                                        persian_author: document.persian_author,
                                                        year: document.year,
                                                        description: document.description,
                                                        persian_description: document.persian_description,
                                                        image_url: document.image_url,
                                                        archive_item_id: document.archive_item_id
                                                    }
                                                }}
                                                className="archive-map-link archive-map-document-link"
                                                onMouseEnter={(e) => {
                                                    e.target.style.textDecoration = 'underline';
                                                    e.target.style.fontWeight = 'bold';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.textDecoration = 'none';
                                                    e.target.style.fontWeight = 'normal';
                                                }}
                                            >
                                                <span className="document-icon">📄</span> {language === 'persian' ? document.persian_name : document.name}
                                            </Link>
                                            
                                            {/* Document Year */}
                                            {document.year && (
                                                <div className="archive-map-years">
                                                    Year: {document.year}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ 
                padding: '20px', 
                textAlign: 'center',
                direction: language === 'persian' ? 'rtl' : 'ltr'
            }}>
                <h1>Archive Map</h1>
                <p>Loading archive data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ 
                padding: '20px', 
                textAlign: 'center',
                direction: language === 'persian' ? 'rtl' : 'ltr'
            }}>
                <h1>Archive Map</h1>
                <p style={{ color: 'red' }}>Error loading archive data: {error}</p>
                <button onClick={fetchArchiveMapData} style={{ marginTop: '10px', padding: '10px' }}>
                    Retry
                </button>
            </div>
        );
    }

    return renderArchiveMap();
}
